import React, { useRef, useEffect, useCallback } from 'react'
import { useSelector } from 'react-redux'
import CTAButton from 'src/components/common/CTAButtonNew'
import { fireUIInteractionEvent } from 'src/modules/analytics/v3'
import { selectStateForProtobuf } from 'src/modules/reducers'
import { selectCTAParams } from 'src/modules/reducers/params'
import { defaultCTAURI } from 'src/modules/utils/constants'
import { createUrl } from 'src/modules/utils/createUrl'
import { replaceUrlParam } from 'src/modules/utils/replaceUrlParam'
import styled from 'styled-components'
import Image from '../Image'
import { heading, typographyMixin } from 'src/constants/marketing-ds/typography'
import { Checkmark } from 'src/components/icons/CheckMark'
import { CrossMark } from 'src/components/icons/CrossMark'
import { colors } from 'src/constants/marketing-ds'

const HeroCard = ({
  promoImg,
  plan,
  length,
  title,
  subtitle,
  currency,
  price,
  discountedPrice,
  savingsNote,
  // listItems will need to be an array of objects of {string, boolean}
  listItems,
  offerCode,
  ctaUrl,
  ctaText,
  asterisk,
  footnote,
  onClick,
  btnColor,
  banner,
  networkLogos,
  showNetworks,
  isFootnoteInCard,
  cardClassName,
  lang = 'en',
  prePriceText,
  backgroundImage,
}) => {
  const parsedParams = useSelector(selectCTAParams)
  const stateForProtobuf = useSelector(selectStateForProtobuf)

  const generateCTAURL = useCallback(
    (offerCode) => {
      let ctaURL = createUrl(defaultCTAURI, parsedParams)
      ctaURL = offerCode ? replaceUrlParam(ctaURL, 'offer', offerCode) : ctaURL
      ctaURL = replaceUrlParam(ctaURL, 'comp', 'plan-card')
      return ctaURL
    },
    [parsedParams]
  )

  const ctaURL = generateCTAURL(offerCode)

  const handleOnClick = (offerCode) => {
    fireUIInteractionEvent(
      {
        element: offerCode,
        component: 'plan_card_cta_clicked',
      },
      stateForProtobuf
    )
  }

  const footnoteInCardRef = useRef(null)
  const monthLang = lang === 'es' ? 'mes' : 'mo'

  useEffect(() => {
    const matchHeights = () => {
      // Only match heights if screen is larger than mobile breakpoint
      if (window.innerWidth <= 576) {
        // Reset heights on mobile
        if (footnoteInCardRef.current) footnoteInCardRef.current.style.minHeight = 'auto'
        return
      }

      // Desktop behavior
      if (footnoteInCardRef.current) footnoteInCardRef.current.style.minHeight = 'auto'

      const footnotes = document.querySelectorAll('.planCard__footnote_in_card')
      let maxHeight = 0
      footnotes.forEach((footnote) => {
        const height = footnote.getBoundingClientRect().height
        maxHeight = Math.max(maxHeight, height)
      })

      footnotes.forEach((footnote) => {
        footnote.style.minHeight = `${maxHeight}px`
      })
    }

    // Initial match
    matchHeights()

    // Add resize listener
    window.addEventListener('resize', matchHeights)

    // Cleanup
    return () => window.removeEventListener('resize', matchHeights)
  }, [footnote])

  return (
    <StyledPlanCardWithImage
      className={`planCard-container ${cardClassName || ''}`}
      backgroundImage={backgroundImage}
    >
      <div className={`planCard ${promoImg ? 'planCard-with-image' : ''}`}>
        {banner && <div className="planCard__banner">{banner}</div>}
        <div className={'planCard__image ' + (banner ? ' planCard__image-top-space' : '')}>
          {promoImg && <Image src={promoImg} alt={title} />}
        </div>
        <div className="planCard__content">
          <div className="planCard__header">
            <div className="planCard__title">{title}</div>
            <div className="planCard__divider"></div>
            {savingsNote && (
              <div className="discount__note">
                {typeof savingsNote === 'string' && <span>{savingsNote}</span>}
              </div>
            )}
          </div>
          <div className="planCard__list">
            {listItems?.map((item, index) => {
              if (item === null) return null
              const check =
                item?.available === undefined ? (
                  item.isAllMatches && item.ctaText ? (
                    <CTAButton
                      as="a"
                      color={btnColor && '#ffffff'}
                      link={generateCTAURL(item.ratePlanCode)}
                      onClick={() => handleOnClick(item.ratePlanCode)}
                    >
                      {item.ctaText}
                    </CTAButton>
                  ) : null
                ) : item?.available ? (
                  <Icon type="check">
                    <Checkmark />
                  </Icon>
                ) : (
                  <Icon type="cross">
                    <CrossMark />
                  </Icon>
                )

              return (
                <div
                  key={index}
                  className={`list-item ${item.isAllMatches && item.ctaText ? 'all-matches-item' : ''}`}
                >
                  {check} {item.text}
                </div>
              )
            })}
          </div>
        </div>
        {ctaText && (
          <div className="planCard_cta">
            <CTAButton
              as="a"
              color={btnColor && '#ffffff'}
              link={ctaURL}
              onClick={() => handleOnClick(offerCode)}
              style={{ marginBottom: '20px' }}
            >
              {ctaText}
            </CTAButton>
          </div>
        )}
        {isFootnoteInCard && (
          <div className="planCard__footnote_in_card" ref={footnoteInCardRef}>
            {footnote}
          </div>
        )}
      </div>
      {!isFootnoteInCard && <div className="planCard__footnote">{footnote}</div>}
    </StyledPlanCardWithImage>
  )
}

export default HeroCard

const StyledImg = styled.img`
  max-height: ${(props) => `${props.customHeight}px`};
  max-width: ${(props) => `${props.customWidth}px`};
  @media (max-width: 576px) {
    max-height: ${(props) => `${props.customHeight * 0.9}px`};
    max-width: ${(props) => `${props.customWidth * 0.9}px`};
  }
`

const StyledPlanCardWithImage = styled.div`
  ${'' /* width: 100%; */}
  width: 470px;
  .planCard {
    ${'' /* width: 460px; */}
    display: flex;
    flex-direction: column;
    height: 100%;
    position: relative;
    padding-top: 10%;
    padding-bottom: 10%;
    border-radius: 12px;
    overflow: hidden;
    box-sizing: border-box;
    background: #0e0330;
    background-image: url(${(props) =>
      props.backgroundImage ||
      'https://assets-imgx.fubo.tv/marketing/lp/images/card/ppv_card_red_bg.png?ch=width&auto=format,compress'});
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;

    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: linear-gradient(
        193deg,
        rgba(0, 2, 21, 0) 16.24%,
        rgba(0, 2, 21, 0.7) 35.26%,
        #000215 57.2%
      );
      z-index: 1;
    }

    @media (max-width: 576px) {
      padding-bottom: 8%;
      width: 90%;
    }

    .planCard__content {
      display: flex;
      flex-direction: column;
      position: relative;
      z-index: 2;
      padding-left: 8%;
      padding-right: 8%;
      color: #fff;
      .planCard__header {
        .planCard__title {
          ${typographyMixin(heading.md)}
          text-align: left;
        }
        .planCard__subtitle {
          font-size: 20px;
          line-height: 1.4;
          font-weight: 500;
        }
        .planCard__divider {
          width: 50px;
          height: 1px;
          background: #fff;
          margin: 24px 0;
        }
        .planCard__price {
          font-size: 24px;
          font-weight: 700;
          @media (max-width: 576px) {
            font-size: 20px;
          }
        }
        .faded-price {
          font-size: 18px;
          margin-left: 4px;
          color: rgba(255, 255, 255, 0.8);
          text-decoration: line-through;
          font-weight: 400;
          -webkit-text-decoration-color: #fa4616;
          text-decoration-color: #fa4616;
        }
        .planCard__networks {
          display: flex;
          flex-direction: row;
          gap: 18px;
          height: 50px;
          margin-top: 30px;
          margin-bottom: 40px;
          @media (max-width: 576px) {
            gap: 15px;
          }
        }
        .planCard__network-logo {
          display: flex;
          align-items: center;
          gap: 6px;

          img {
            ${
              '' /* max-height: 30px;
            max-width: 100px; */
            }
          }
          .planCard__network-logo-text {
            font-size: 18px;
            font-weight: 700;
            white-space: nowrap;
            @media (max-width: 500px) {
              font-size: 14px;
            }
          }
        }
        .discount__note {
          font-size: 0.75rem;
          font-weight: 600;
          letter-spacing: 0.01em;
          line-height: 1.25;
          text-decoration: none;
          text-transform: none;
          color: rgb(255, 255, 255);
          display: inline-block;
          background-color: rgb(0, 136, 23);
          border-radius: 4px;
          span {
            display: inline-block;
            padding: 4px 8px;
          }
        }
      }

      .planCard__list {
        display: flex;
        flex-direction: column;
        margin-bottom: 10px;
        @media (max-width: 576px) {
          padding-bottom: 20px;
        }
        .list-item {
          font-size: 15px;
          line-height: 1.3;
          font-weight: 600;
          letter-spacing: 0px;
          margin-bottom: 14px;
          display: flex;
          span {
            width: 20px;
            margin-right: 8px;
            line-height: 1;
            flex: 0 0 20px;
          }
          .check {
            color: #04a928;
            font-size: 18px;
          }
          .cross {
            color: #ff0000;
            font-size: 20px;
          }
          &.all-matches-item {
            margin: 0;
          }
          .CTAButton {
            margin: 15px auto 30px;
            display: block;
            width: 100%;

            @media (min-width: 650px) {
              width: 80%;
            }

            .btn-content {
              font-size: 18px;
              width: 100%;
              text-align: center;

              @media (min-width: 650px) {
                font-size: 20px;
              }
            }
          }
        }
      }
    }
    .planCard_cta {
      display: flex;
      flex-direction: column;
      position: relative;
      z-index: 2;
      padding-left: calc(11%);
      padding-right: calc(11%);
      height: 100%;
      justify-content: flex-end;
    }
    .planCard__footnote_in_card {
      font-size: 14px;
      font-weight: 500;
      text-align: center;
      padding-left: 8%;
      padding-right: 8%;
      z-index: 2;
    }
  }
  .planCard__footnote {
    font-size: 14px;
    font-weight: 500;
    text-align: center;
    margin-top: 20px;
    margin-bottom: 20px;
  }
`

const Icon = styled.span`
  width: 16px;
  height: 16px;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  flex-shrink: 0;
  color: ${({ type }) => (type === 'check' ? colors.mint[80] : colors.red[120])};
`
